import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, HelpCircle, Euro, Shield, Building2, Users, Mail, Phone, CreditCard } from 'lucide-react';

const FAQ_DATA = [
  {
    category: 'Generale',
    icon: HelpCircle,
    questions: [
      {
        q: "Cos'è ARTEGO.APP - Amministro Condomini?",
        a: "ARTEGO.APP è una piattaforma cloud completa per la gestione condominiale. Permette agli amministratori di condominio di gestire in modo efficiente tutte le attività: dalla contabilità alla comunicazione con i condomini, dalla gestione documentale alle assemblee."
      },
      {
        q: "A chi è rivolto il servizio?",
        a: "Il servizio è rivolto ad amministratori di condominio professionisti, studi di amministrazione, e property manager che gestiscono uno o più stabili. È ideale anche per condomini che desiderano un'amministrazione più trasparente e digitale."
      },
      {
        q: "Il software è conforme al GDPR?",
        a: "Sì, ARTEGO.APP è pienamente conforme al Regolamento Europeo sulla Protezione dei Dati (GDPR). Implementiamo misure di sicurezza avanzate, crittografia dei dati, backup automatici e procedure di data retention personalizzabili."
      },
      {
        q: "Posso accedere da dispositivi mobili?",
        a: "Assolutamente sì. L'applicazione è completamente responsive e ottimizzata per smartphone e tablet. Puoi gestire il tuo condominio ovunque ti trovi, con la stessa esperienza utente del desktop."
      }
    ]
  },
  {
    category: 'Pagamenti Online (Amministratori)',
    icon: CreditCard,
    questions: [
      {
        q: "Come posso abilitare i pagamenti online con carta di credito o bancomat per i condomini?",
        a: `Per abilitare i pagamenti online con carta di credito, bancomat o altri metodi elettronici, è necessario seguire questi passaggi:

1. **Scegli un provider di pagamento** compatibile (SumUp, Nexi, Stripe, PayPal Business, ecc.)
2. **Attiva un account** presso il provider scelto e ottieni le tue credenziali API
3. **Contatta il nostro supporto** inviando una email a support.app@artegosrls.it con oggetto "Attivazione Pagamenti Online"
4. **Fornisci le credenziali API** del tuo servizio di pagamento (chiave API, Merchant ID, ecc.)
5. Il nostro team configurerà il sistema entro 24-48 ore lavorative

Una volta configurato, i tuoi condomini potranno pagare le quote direttamente dall'app con carta di credito, debito o bancomat.`
      },
      {
        q: "Quali provider di pagamento sono supportati?",
        a: `Attualmente supportiamo i seguenti provider di pagamento:

• **SumUp** - Ideale per piccoli/medi studi, commissioni competitive
• **Nexi** - Il principale provider italiano, ampia diffusione
• **Stripe** - Ottimo per chi gestisce molti pagamenti internazionali
• **PayPal Business** - Molto conosciuto dai condomini

Se utilizzi un provider diverso, contattaci: valuteremo l'integrazione in base alle richieste.`
      },
      {
        q: "Quali informazioni devo fornire per l'attivazione?",
        a: `Per attivare i pagamenti online, invia una email a support.app@artegosrls.it con le seguenti informazioni:

**Dati obbligatori:**
• Nome dello studio/amministratore
• Provider di pagamento scelto (es. SumUp, Nexi)
• Chiave API (API Key) del provider
• Merchant ID o Account ID
• Email associata all'account del provider

**Per SumUp:**
• API Key (generabile dal pannello SumUp Business)
• Merchant Code

**Per Nexi:**
• Terminal ID
• Alias
• Chiave MAC di sicurezza
• Ambiente (Test/Produzione)

**Per Stripe:**
• Publishable Key
• Secret Key

Il nostro team ti guiderà nel processo di recupero delle credenziali se necessario.`
      },
      {
        q: "Quanto costa abilitare i pagamenti online?",
        a: `L'attivazione dei pagamenti online su ARTEGO.APP è **gratuita**. 

Le uniche commissioni applicate sono quelle del provider di pagamento scelto (es. SumUp, Nexi), che variano tipicamente dall'1% al 2.5% per transazione.

Non applichiamo commissioni aggiuntive sulle transazioni processate tramite la nostra piattaforma.`
      },
      {
        q: "I condomini possono pagare anche con bonifico bancario?",
        a: `Sì, oltre ai pagamenti con carta puoi configurare il **bonifico bancario** come metodo alternativo.

Per abilitarlo:
1. Vai su **Impostazioni → Pagamenti** nel tuo pannello amministratore
2. Inserisci le coordinate bancarie del conto condominiale (IBAN, intestatario, banca)
3. Salva le impostazioni

I condomini vedranno automaticamente entrambe le opzioni: "Paga con Carta" e "Bonifico Bancario" con le coordinate precompilate e la causale corretta.`
      },
      {
        q: "Come ricevo notifica quando un condomino paga online?",
        a: `Quando un condomino effettua un pagamento online, ricevi automaticamente:

• **Notifica in-app** visibile nella campanella delle notifiche
• **Email di conferma** con tutti i dettagli del pagamento
• **Notifica push** (se attivata nelle impostazioni)

Inoltre, la quota viene automaticamente contrassegnata come "SALDATA" nel sistema, evitandoti di doverla registrare manualmente.`
      },
      {
        q: "Posso disabilitare i pagamenti online per un singolo condominio?",
        a: `Sì, le impostazioni di pagamento sono configurabili per ogni singolo condominio.

Puoi scegliere di:
• Abilitare solo il pagamento con carta
• Abilitare solo il bonifico bancario  
• Abilitare entrambi
• Disabilitare completamente i pagamenti online

Vai su **Impostazioni → Pagamenti** e seleziona il condominio da configurare.`
      },
      {
        q: "Cosa succede se un pagamento non va a buon fine?",
        a: `In caso di pagamento non riuscito:

1. Il condomino riceve un messaggio di errore chiaro
2. La quota rimane nello stato "DA PAGARE"
3. Non viene addebitato alcun importo
4. Il condomino può riprovare immediatamente

Se i problemi persistono, contatta il nostro supporto o verifica lo stato del tuo account presso il provider di pagamento.`
      },
      {
        q: "Come contatto il supporto per l'attivazione?",
        a: `Per attivare i pagamenti online o ricevere assistenza:

📧 **Email:** support.app@artegosrls.it
📞 **Telefono:** +39 3520498295

**Orari supporto:** Lun-Ven 9:00-18:00

Ti consigliamo di contattarci via email includendo tutte le informazioni richieste per velocizzare l'attivazione.`
      }
    ]
  },
  {
    category: 'Piani e Prezzi',
    icon: Euro,
    questions: [
      {
        q: "Quali sono i piani di abbonamento disponibili?",
        a: "Offriamo tre piani: STARTER (fino a 3 condomini), PROFESSIONAL (fino a 10 condomini) e ENTERPRISE (condomini illimitati). Ogni piano include tutte le funzionalità base, con limiti diversi su numero di condomini, spazio di archiviazione e supporto."
      },
      {
        q: "Quanto costa il canone di noleggio?",
        a: "Il canone varia in base al piano scelto: STARTER da €29/mese, PROFESSIONAL da €79/mese, ENTERPRISE da €149/mese. Sono disponibili sconti per pagamento annuale anticipato (2 mesi gratis). Contattaci per preventivi personalizzati su grandi volumi."
      },
      {
        q: "È previsto un periodo di prova gratuito?",
        a: "Sì, offriamo 14 giorni di prova gratuita su tutti i piani senza necessità di carta di credito. Durante il periodo di prova avrai accesso a tutte le funzionalità del piano scelto."
      },
      {
        q: "Posso cambiare piano in qualsiasi momento?",
        a: "Sì, puoi effettuare upgrade o downgrade del tuo piano in qualsiasi momento. L'upgrade è immediato, mentre il downgrade sarà effettivo dal successivo ciclo di fatturazione."
      },
      {
        q: "Cosa succede ai miei dati se cancello l'abbonamento?",
        a: "I tuoi dati rimangono disponibili per 30 giorni dalla cancellazione, permettendoti di esportare tutto. Dopo questo periodo, i dati vengono eliminati definitivamente dai nostri server, come previsto dalla normativa GDPR."
      }
    ]
  },
  {
    category: 'Funzionalità',
    icon: Building2,
    questions: [
      {
        q: "Quali funzionalità include la gestione contabile?",
        a: "La gestione contabile include: registrazione fatture e spese, ripartizione automatica tramite tabelle millesimali, generazione quote condominiali (sistema preventivo o consuntivo), gestione ritenute d'acconto con F24 e Modello 770, riconciliazione bancaria, bilanci preventivi e consuntivi."
      },
      {
        q: "Come funziona l'invio automatico delle quote ai condomini?",
        a: "Puoi configurare l'invio automatico delle quote mensili via email. Imposta il giorno del mese preferito, scegli se allegare il PDF del bollettino, il dettaglio delle voci di spesa e eventualmente un link per il pagamento online. Il sistema invierà tutto automaticamente."
      },
      {
        q: "Posso gestire le assemblee condominiali?",
        a: "Sì, il modulo Assemblee permette di: creare convocazioni con ordine del giorno, inviare avvisi ai condomini, gestire deleghe, registrare presenze e votazioni, generare verbali automatici con esiti delle delibere."
      },
      {
        q: "Come funziona la gestione documentale?",
        a: "Tutti i documenti del condominio sono archiviati in cloud con categorizzazione automatica. Puoi caricare contratti, verbali, fatture, documenti tecnici. I condomini possono accedere ai documenti di loro competenza tramite il portale dedicato."
      },
      {
        q: "È possibile gestire il personale dello stabile?",
        a: "Sì, il modulo Personale permette di gestire dipendenti (portieri, giardinieri, ecc.), registrare presenze, gestire assenze e ferie, caricare buste paga e F24 del personale. I dipendenti hanno accesso a un portale dedicato."
      },
      {
        q: "Come funzionano le segnalazioni guasti?",
        a: "Condomini e portieri possono segnalare guasti, sinistri o richieste di manutenzione direttamente dall'app. L'amministratore riceve una notifica immediata e può tracciare lo stato di risoluzione di ogni segnalazione."
      }
    ]
  },
  {
    category: 'Sicurezza e Privacy',
    icon: Shield,
    questions: [
      {
        q: "Come sono protetti i miei dati?",
        a: "I dati sono protetti con crittografia AES-256 sia in transito (HTTPS/TLS 1.3) che a riposo. I nostri server sono ospitati in data center certificati ISO 27001 in Europa. Eseguiamo backup automatici giornalieri con retention di 30 giorni."
      },
      {
        q: "Chi può accedere ai dati del condominio?",
        a: "Solo gli utenti autorizzati con credenziali personali. Il sistema prevede ruoli differenziati: Amministratore (accesso completo), Contabile, Consulente del Lavoro, Condomino (solo propri dati), Dipendente (solo proprio portale)."
      },
      {
        q: "Posso esportare i miei dati?",
        a: "Sì, in qualsiasi momento puoi esportare tutti i dati in formati standard (PDF, CSV, Excel). Questa funzionalità è disponibile per ogni sezione dell'applicazione e garantisce la portabilità dei dati come previsto dal GDPR."
      }
    ]
  },
  {
    category: 'Supporto',
    icon: Users,
    questions: [
      {
        q: "Che tipo di supporto è incluso?",
        a: "Tutti i piani includono supporto via email. I piani PROFESSIONAL e ENTERPRISE includono anche supporto telefonico prioritario e accesso a webinar formativi. Il piano ENTERPRISE include un account manager dedicato."
      },
      {
        q: "Come posso contattare l'assistenza?",
        a: "Puoi contattarci via email a support.app@artegosrls.it, telefonicamente al +39 3520498295, o tramite il bottone 'Supporto' presente nell'applicazione. Rispondiamo entro 24 ore lavorative."
      },
      {
        q: "È disponibile formazione sull'utilizzo del software?",
        a: "Sì, offriamo sessioni di onboarding gratuite per tutti i nuovi clienti. Inoltre, sono disponibili video tutorial, guide online e webinar periodici sulle nuove funzionalità. Per esigenze specifiche, offriamo formazione personalizzata a pagamento."
      },
      {
        q: "Cosa fare in caso di problemi tecnici?",
        a: "In caso di problemi, contatta il supporto descrivendo l'errore riscontrato. Per problemi critici che bloccano l'operatività, il nostro team interviene con priorità massima. Il sistema registra automaticamente gli errori per facilitare la diagnosi."
      }
    ]
  }
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Format answer text with proper line breaks and styling
  const formatAnswer = (text) => {
    // Split by double newlines for paragraphs
    const paragraphs = text.split('\n\n');
    
    return paragraphs.map((paragraph, pIndex) => {
      // Check if this is a list (starts with • or numbers)
      const lines = paragraph.split('\n');
      const isList = lines.some(line => line.trim().startsWith('•') || line.trim().match(/^\d+\./));
      
      if (isList) {
        return (
          <ul key={pIndex} className="list-none space-y-1 my-2">
            {lines.map((line, lIndex) => {
              const trimmedLine = line.trim();
              if (!trimmedLine) return null;
              
              // Check for bold text marked with **
              const formattedLine = trimmedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
              
              return (
                <li 
                  key={lIndex} 
                  className="pl-2"
                  dangerouslySetInnerHTML={{ __html: formattedLine }}
                />
              );
            })}
          </ul>
        );
      }
      
      // Regular paragraph with bold support
      const formattedParagraph = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return (
        <p 
          key={pIndex} 
          className="my-2"
          dangerouslySetInnerHTML={{ __html: formattedParagraph }}
        />
      );
    });
  };
  
  return (
    <div className="border-b border-stone-200 last:border-0">
      <button
        className="w-full flex items-center justify-between py-4 text-left hover:bg-stone-50 px-4 -mx-4 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-slate-900 pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-stone-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-stone-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="artego-prose pb-4 px-4 -mx-4 text-stone-600 text-sm leading-relaxed">
          {formatAnswer(answer)}
        </div>
      )}
    </div>
  );
};

export const FAQ = () => {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Domande Frequenti
        </h1>
        <p className="text-stone-600 mt-2 text-lg">
          Trova risposte alle domande più comuni su ARTEGO.APP
        </p>
      </div>

      {/* Quick Contact */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <HelpCircle className="h-8 w-8 text-blue-600" />
              <div>
                <p className="font-semibold text-slate-900">Non trovi quello che cerchi?</p>
                <p className="text-sm text-stone-600">Il nostro team è a tua disposizione</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = 'mailto:support.app@artegosrls.it'}
                className="bg-white"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email Supporto
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = 'tel:+393520498295'}
                className="bg-white"
              >
                <Phone className="h-4 w-4 mr-2" />
                Chiama
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQ Categories */}
      {FAQ_DATA.map((category) => {
        const Icon = category.icon;
        return (
          <Card key={category.category}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Icon className="h-5 w-5 text-slate-700" />
                </div>
                <CardTitle className="text-xl">{category.category}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-stone-200">
                {category.questions.map((item, index) => (
                  <FAQItem key={index} question={item.q} answer={item.a} />
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Footer CTA */}
      <Card className="bg-slate-900 text-white">
        <CardContent className="py-8 text-center">
          <h2 className="text-2xl font-bold mb-2">Pronto a iniziare?</h2>
          <p className="text-slate-300 mb-6">
            Prova ARTEGO.APP gratuitamente per 14 giorni, senza impegno.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              className="bg-white text-slate-900 hover:bg-slate-100"
              onClick={() => window.location.href = '/register'}
            >
              Inizia la Prova Gratuita
            </Button>
            <Button 
              variant="outline" 
              className="border-white text-white hover:bg-white/10"
              onClick={() => window.location.href = 'mailto:info@artegosrls.it'}
            >
              Richiedi Demo
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FAQ;
